import type {
  IntegrationGroup,
  IntegrationItemDto,
  IntegrationKind,
  IntegrationTestResult,
} from '@fos/shared/contracts/integrations';
import {
  GOOGLE_CALENDAR_TITLE,
  ICS_CALENDAR_TITLE,
  INTEGRATION_KIND_DESCRIPTIONS,
  INTEGRATION_KIND_LABELS,
  INTEGRATION_STATUS_LABELS,
  INTEGRATION_STATUS_TONES,
  integrationProviderLabel,
} from '@fos/shared/labels';
import { Activity, CalendarDays, HardDrive, type LucideIcon, Mail, Receipt, Send, Sparkles } from 'lucide-react';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { formatRelativePast } from '../../../lib/format';
import type { Tone } from '../../../ui/Status';
import { toast } from '../../../ui/Toast';

/** Ikona rodzaju (Figma 12: kafelek 36 px na bg/surface, ikona 18 px). */
export const INTEGRATION_ICONS: Record<IntegrationKind, LucideIcon> = {
  gmail: Mail,
  google_calendar: CalendarDays,
  invoicing: Receipt,
  ai: Sparkles,
  smtp: Send,
  storage: HardDrive,
  monitoring: Activity,
};

export const GROUP_ORDER: IntegrationGroup[] = ['company', 'mine', 'member'];

export function kindLabel(kind: IntegrationKind): string {
  return INTEGRATION_KIND_LABELS[kind];
}

/**
 * Nazwa pozycji: kalendarz z linku iCal (domyślny sposób, DECISIONS 2026-10-07 „Bez Google Workspace”) albo
 * połączony kontem Google; pozostałe rodzaje — nazwa rodzaju.
 */
export function itemTitle(item: Pick<IntegrationItemDto, 'kind' | 'provider'>): string {
  if (item.kind === 'google_calendar') return item.provider === 'google' ? GOOGLE_CALENDAR_TITLE : ICS_CALENDAR_TITLE;
  return kindLabel(item.kind);
}

/** Kalendarz z tajnego adresu iCal (zapisany albo karta „Niepodłączona” kalendarza). */
export function isIcsCalendar(item: Pick<IntegrationItemDto, 'kind' | 'provider'>): boolean {
  return item.kind === 'google_calendar' && (item.provider === 'ics' || item.provider === null);
}

export function statusText(item: IntegrationItemDto): string {
  return INTEGRATION_STATUS_LABELS[item.status];
}

export function statusTone(item: IntegrationItemDto): Tone {
  return INTEGRATION_STATUS_TONES[item.status] as Tone;
}

/**
 * Podpis pod nazwą (Figma 12: „kontakt@fravity.pl · synchronizacja 2 min temu”, „Wystawianie faktur z zestawień
 * czasu”): konto / subdomena / serwer, dostawca i ostatnia synchronizacja; bez danych — opis rodzaju.
 */
export function itemDescription(item: IntegrationItemDto, now: Date = new Date()): string {
  const parts: string[] = [];
  if (item.summary) parts.push(item.summary);
  if (item.provider && !item.simulated && item.status !== 'disconnected' && !item.summary) {
    parts.push(integrationProviderLabel(item.provider));
  }
  if (item.group === 'member' && item.ownerName) parts.unshift(item.ownerName);
  if (item.lastSyncAt) parts.push(`synchronizacja ${formatRelativePast(item.lastSyncAt, now)}`);
  return parts.length > 0 ? parts.join(' · ') : INTEGRATION_KIND_DESCRIPTIONS[item.kind];
}

export function isGoogleKind(kind: IntegrationKind): boolean {
  return kind === 'gmail' || kind === 'google_calendar';
}

/** Skrzynka pocztowa (rodzaj `gmail`): Gmail przez Google albo IMAP/SMTP (np. Zenbox) z formularza. */
export function isMailboxKind(kind: IntegrationKind): boolean {
  return kind === 'gmail';
}

/** Połączenie przez konto Google (skrzynka Gmail, kalendarz przez OAuth — alternatywa dla linku iCal). */
export function usesGoogleAccount(item: Pick<IntegrationItemDto, 'kind' | 'provider'>): boolean {
  return item.provider === 'gmail' || item.provider === 'google';
}

/** Nowa skrzynka (karta „Dodaj skrzynkę pocztową”): pusta pozycja z dostawcami rodzaju z listy. */
export function newMailboxItem(base: IntegrationItemDto): IntegrationItemDto {
  return {
    ...base,
    key: 'new:gmail',
    id: null,
    provider: null,
    status: 'disconnected',
    simulated: false,
    group: 'company',
    managedBy: 'app',
    authMethod: null,
    ownerUserId: null,
    ownerName: null,
    config: {},
    summary: null,
    hasSecret: false,
    secretLast4: null,
    secretSetAt: null,
    connectedBy: null,
    connectedAt: null,
    lastSyncAt: null,
    lastError: null,
    version: null,
    canManage: true,
    canDisconnect: false,
    canTest: false,
  };
}

/** Serwer i port z konfiguracji skrzynki IMAP/SMTP (np. „imap.hosting.pl:993”). */
export function serverOf(config: IntegrationItemDto['config'], kind: 'imap' | 'smtp'): string | null {
  const host = config[`${kind}Host`];
  const port = config[`${kind}Port`];
  if (typeof host !== 'string' || !host) return null;
  return typeof port === 'number' ? `${host}:${port}` : host;
}

/** Toast z wynikiem testu połączenia (symulacja zawsze oznaczona). */
export function testResultToast(result: IntegrationTestResult): void {
  if (result.ok)
    toast.success(result.simulated ? 'Działa w symulacji' : 'Połączenie działa', { description: result.message });
  else toast.error('Połączenie nie działa', { description: result.message });
}

/** Błąd mutacji jako toast (anulowane potwierdzenie hasłem nie jest błędem do pokazania). */
export function toastMutationError(err: unknown): void {
  if (appErrorCode(err) === 'STEP_UP_REQUIRED') return;
  toast.error(errorMessage(err));
}
